// pages/OurWorkPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GalleryItem } from '../types';
import { galleryItems } from '../data/constants';
import Button from '../components/ui/Button';
import Section from '../components/layout/Section';
import Modal from '../components/ui/Modal';
import { trackCtaClick } from '../utils/tracking';

const OurWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);

  const openModal = (item: GalleryItem) => {
    setSelectedImage(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  return (
    <>
      <div className="pt-20">
        <Section title="Our Recent Residential Projects" bg="bg-slate-50">
          <p className="text-center text-lg text-gray-700 mb-12 max-w-3xl mx-auto">
            At Lemay Roofing, our work speaks for itself. Browse our gallery to see the quality craftsmanship and variety of residential roofing projects we've completed for satisfied homeowners. Click on an image to see a larger view.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden group transform hover:scale-105 transition-transform duration-300 cursor-pointer"
                onClick={() => openModal(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(item);}}
              >
                <img
                  src={item.thumbnailUrl}
                  alt={item.altText + " - thumbnail"}
                  className="w-full h-52 object-cover"
                  onError={(e) => (e.currentTarget.src = 'https://placehold.co/400x300/cccccc/333333?text=Thumb+Not+Found')}
                />
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-slate-800 truncate">{item.caption}</h3>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Button onClick={() => { trackCtaClick('/contact', 'Discuss Your Project With Us'); navigate('/contact'); }} variant="secondary">
              Discuss Your Project With Us
            </Button>
          </div>
        </Section>
      </div>

      {selectedImage && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          imageUrl={selectedImage.fullUrl}
          altText={selectedImage.altText}
          caption={selectedImage.caption}
        />
      )}
    </>
  );
};

export default OurWorkPage;
